import { call } from "redux-saga/effects";

import { API_URL } from "..";
import { baseRequest } from "../request";
import { REFRESH_REQUEST_TIMEOUT_MS, abortRefreshInFlight } from "./refresh_token_action";
import {
  abandonBootLogout,
  isBootLogoutInFlight,
  isLoginInFlight,
  isLogoutPending,
  logoutPendingGeneration,
  logoutPendingValue,
  recordLogoutReply,
  setLogoutPending,
  trackBootLogout,
  trackLoginAttempt,
  waitForBootLogout,
} from "./logout_pending";

// The retry of a logout the server never confirmed: at boot, when the browser comes back online,
// when a tab comes into view, and when the refresh coordinator finds the marker. Every one of them
// is this one request, so a login can wait for whichever is on the wire.

// It goes straight at `baseRequest` rather than through `clearIdentitySession`, whose `finally`
// wipes `labfront` and could therefore race a login the user starts in this same tab. Best effort:
// the marker is all that has to survive, and it only goes on the server's definitive answer (a 2xx,
// 401 or 404, see `isDefinitiveLogoutReply`).
const sendPendingLogout = (signal: AbortSignal): Promise<void> => {
  // this reply can land long after it was sent: the marker exists *because* a previous logout hung,
  // so the retry is likely to hang too. Every write is skipped once the reply no longer speaks for
  // the marker this retry found:
  // - a login in this tab abandoned the request at the 3s cap (`waitForBootLogout`). That is no
  //   answer at all, and rewriting the marker would end the session of every sibling tab.
  // - the generation moved: a late failure must not re-arm the marker over a login in this tab.
  // - the stored value changed: a login in another tab removes the shared marker without moving
  //   the generation, and a sign-out there replaces it with a newer one.
  const generation = logoutPendingGeneration();
  const retryMarker = logoutPendingValue();
  const speaksForThisRetry = () =>
    !signal.aborted &&
    logoutPendingGeneration() === generation &&
    logoutPendingValue() === retryMarker;

  return baseRequest({
    method: "post",
    url: `${API_URL}/v1/web/identity-logout`,
    timeout: REFRESH_REQUEST_TIMEOUT_MS,
    signal,
  })
    .then((result) => {
      if (speaksForThisRetry()) recordLogoutReply(result?.statusCode);
    })
    .catch(() => {
      if (speaksForThisRetry()) setLogoutPending();
    });
};

// a login in this tab waits for the retry it is handed here, so the logout's `Set-Cookie` cannot
// land after the login's. The boot calls this directly: a tab that boots with the marker retries
// whether or not it is in view, and so does a boot past the idle limit once the server has answered,
// which can be long after the boot. A login in progress has already passed its wait, so nothing goes
// out while one holds its claim.
export const startPendingLogout = (): void => {
  if (isLoginInFlight()) return;
  const controller = new AbortController();
  trackBootLogout(sendPendingLogout(controller.signal), controller);
};

// Without the later retries a sign-out that failed on a flaky connection waits for the next boot,
// and a tab left open on the login page keeps a session the server still holds live until it is
// reloaded.
//
// `online` only means the OS sees a network, not that the API answers. The refresh coordinator
// treats it as a hint for its own refresh; acting on it directly here is deliberate, because a
// wasted logout is harmless and a skipped one is the bug these retries exist for.
//
// Only the tab in view retries. The marker is shared, so every open tab would otherwise send its
// own logout at once, and a login waits only for the one in its own tab: a background tab's reply
// could clear the cookie a login in the tab in view had just set. That is also why a tab sent to the
// background abandons the retry it already has out. Two windows side by side, both in view, and a
// switch to another tab while a sign-in is still on the wire, are the cases this leaves open.
//
// No debounce: each trigger needs an `offline`, a tab switch or a refresh attempt in between, and
// while a retry -- this one or the boot's -- is still out, it is dropped. A second request would
// slip past the login's wait, which only knows the one it was handed. A login in progress drops it
// for the same reason: it has already passed its wait (`trackLoginAttempt`).
export const retryPendingLogout = (): void => {
  if (document.visibilityState !== "visible") return;
  if (!isLogoutPending() || isBootLogoutInFlight() || isLoginInFlight()) return;
  startPendingLogout();
};

export const retryPendingLogoutOnVisibilityChange = (): void => {
  if (document.visibilityState === "hidden") {
    abandonBootLogout();
    return;
  }
  retryPendingLogout();
};

// Every saga whose request answers with a new session's refresh cookie runs it inside this: it holds
// the login claim, waits for a logout retry in flight (3s cap, then abandoned) so that logout's
// `Set-Cookie` cannot land after this one, abandons a refresh still out for the same reason, and
// gives the claim back however the saga ends. The saga itself retires the marker once its session
// is set. A request that ends without a session leaves the marker set, and a trigger dropped while
// the claim was held is not queued, so the retry runs once straight away instead of waiting for the
// next one.
export function* withLoginClaim<T>(
  startSession: () => Generator<any, T, any>,
): Generator<any, T, any> {
  const releaseLoginClaim = trackLoginAttempt();
  try {
    yield call(waitForBootLogout);
    abortRefreshInFlight();
    return yield* startSession();
  } finally {
    releaseLoginClaim();
    retryPendingLogout();
  }
}
